import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { SmartphoneIcon } from "lucide-react";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  useResetUniqueContentMutation,
  useUpdateUniqueContentMutation,
} from "@/data/mutations";
import { useCMSStore } from "@/hooks/use-cms-store";
import { FileType, UploadFileOptions } from "@/lib/uploads";

import { FileUploadInput } from "../sku/file-upload-input";

const cmsAdvancedFormSchema = z.object({
  csv_file_url: z.string(),
});

export const CMSAdvanced = () => {
  const { skuId } = useParams<{ skuId: string }>();
  const uniqueContent = useCMSStore.use.uniqueContent();
  const updateUniqueContentMutation = useUpdateUniqueContentMutation(skuId!);
  const resetUniqueContentMutation = useResetUniqueContentMutation(skuId!);

  const form = useForm({
    resolver: zodResolver(cmsAdvancedFormSchema),
    values: {
      csv_file_url: get(uniqueContent, "metadata.file_name", ""),
    },
  });

  const handleUploadFile = useCallback(
    (options: UploadFileOptions) => {
      return updateUniqueContentMutation.mutateAsync({
        ...options,
        skuId: skuId!,
      });
    },
    [skuId, updateUniqueContentMutation],
  );

  // When the file is removed, we delete the unique content from the SKU
  // and remove all column mappings present in the page_content
  const handleRemoveFile = useCallback(() => {
    return resetUniqueContentMutation.mutateAsync();
  }, [resetUniqueContentMutation]);

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Unique Content</CardTitle>
          <CardDescription className="font-medium">
            Upload a CSV file with the content that can be mapped to each unique
            tag in the SKU.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <FormField
              name="csv_file_url"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CSV</FormLabel>
                  <FormControl>
                    <FileUploadInput
                      {...field}
                      fileType={FileType.CSV}
                      onUploadFile={handleUploadFile}
                      onRemoveFile={handleRemoveFile}
                      previewDisabled
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </Form>
        </CardContent>

        <div className="border-t px-6 py-4">
          <Button variant="secondary" asChild>
            <Link to={`/skus/${skuId}/web-experience/content`}>
              <SmartphoneIcon /> Go to Content
            </Link>
          </Button>
        </div>
      </Card>
    </div>
  );
};
